<script setup>
import { ref } from 'vue'
import BcsModal from './BcsModal.vue'
import BcsDynamicText from './BcsDynamicText.vue'
import EmployerBasicInfoForm from './common/EmployerBasicInfoForm.vue'

// Props for modal configuration and form data
const props = defineProps({
  employerData: { type: Object, required: true },
  checkDuplicateEmployer: { type: Boolean, default: false },
  isNameTaken: { type: Function, default: () => false },
  goToExistingEmployer: { type: Function, required: true },
  duplicateEmployerMatch: { type: [Object, Array], default: () => null },
  forceUpdate: { type: Boolean, default: false },
  continueEditEmployer: { type: Function, required: true },
  actualEmployer: { type: Object, default: null },
  hasEmptyValues: { type: Boolean, default: false },
  isSubmitting: { type: Boolean, default: false }
})
const emit = defineEmits(['update', 'close', 'submit'])

// Reference to the modal instance
const modalRef = ref(null)

/**
 * Open the modal
 */
function openModal() {
  modalRef.value.openModal()
}

/**
 * Close the modal and emit close event
 */
function closeModal() {
  modalRef.value.closeModal()
  emit('close')
}

/**
 * Handle form updates and emit update event
 */
function onFormUpdate(formState) {
  emit('update', formState)
}

/**
 * Handle submit button click
 */
function onSubmit() {
  emit('submit')
}

// Expose openModal and closeModal for parent ref
defineExpose({ openModal, closeModal })
</script>

<template>
  <BcsModal
    ref="modalRef"
    modalSize="small"
    :modalType="'confirmation'"
    :hideCloseButton="true"
    :showFooter="false"
    :noDefaultStyles="true"
    @closeButtonClicked="closeModal"
  >
    <template #header>
      <div class="w-100 px-4 py-3 create-update-employer-header">
        <div class="d-flex justify-content-between align-items-center pt-0 mb-1">          
          <span class="modal-title header-22">
            <strong>{{ actualEmployer ? 'Edit Employer' : 'Add New Employer' }}</strong>
          </span>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <p class="mb-0 text-start text-14 mt-1">
          <BcsDynamicText
            :text="actualEmployer ? 'editEmployerModalDescriptionText' : 'addEmployerDescriptionText'"
          />
        </p>
      </div>
    </template>

      <div class="text-start px-5 pt-4">
        <EmployerBasicInfoForm
        :employerData="employerData"
        :checkDuplicateEmployer="checkDuplicateEmployer"
        :isNameTaken="isNameTaken"
        :goToExistingEmployer="goToExistingEmployer"
        :duplicateEmployerMatch="duplicateEmployerMatch"
        :forceUpdate="forceUpdate"
        :continueEditEmployer="continueEditEmployer"
        :isEditMode="!!actualEmployer"
        @update="onFormUpdate"
      />
      </div>


    <template #footer-body>
      <div class="d-flex justify-content-end px-4 py-3 mt-5 create-update-employer-footer">
        <button type="button" id="cancelCreateEmployer" class="btn text-primary bg-info me-2" @click="closeModal">
          Cancel
        </button>
        <button
          id="createEmployerButton"
          type="button"
          class="btn btn-primary"
          :disabled="hasEmptyValues || checkDuplicateEmployer || isSubmitting"
          @click="onSubmit"
        >
          {{ actualEmployer ? 'Save Employer Details' : 'Add Employer' }}
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
        </button>
      </div>
    </template>
  </BcsModal>
</template>

<style scoped>
.create-update-employer-footer {
  border: none;
  border-top: 1px solid #ddd;
  margin: 0;
}

.create-update-employer-header {
  border: none;
  border-bottom: 1px solid #ddd;
  margin: 0;
}
</style>
